import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
import {type AssigneePickerAssignee$data as Assignee, AssigneeRepositoryPicker} from '@github-ui/item-picker/assignee'
import {
  LabelPickerBase,
  LabelPickerGraphqlQuery,
  type LabelPickerLabel$data as Label,
  type LabelPickerQuery,
} from '@github-ui/item-picker/label'
import {reportError} from '@github-ui/failbot'
import {
  MilestonePickerBase,
  type MilestonePickerMilestone$data as Milestone,
  type MilestonePickerQuery,
  MilestonesPickerGraphqlQuery,
} from '@github-ui/item-picker/milestone'
import {
  type IssueTypePickerIssueType$data as IssueType,
  type IssueTypePickerQuery,
  IssueTypePickerGraphqlQuery,
  IssueTypePickerBase,
} from '@github-ui/item-picker/issue-type'
import {
  commitUpdateIssueProjectsMutation,
  ProjectPicker,
  ProjectPickerGraphqlQuery,
  type ProjectPickerProject$data as Project,
  type ProjectPickerQuery,
} from '@github-ui/item-picker/project'
import type {RepositoryPickerRepository$data as Repository} from '@github-ui/item-picker/repository'
import {MarkdownEditor, type MarkdownEditorConfig, type Subject, type ViewMode} from '@github-ui/markdown-editor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast'
import {Box, Flash, Octicon, FormControl, TextInput} from '@primer/react'
import {
  type FormEvent,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useQueryLoader, useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {ERRORS, LABELS} from './constants'
import type {CreateIssueInput, createIssueMutation} from './mutations/__generated__/createIssueMutation.graphql'
import {commitCreateIssueMutation} from './mutations/create-issue-mutation'
import {MetadataLoading} from './MetadataLoading'
import type {IssueCreatePayload} from '.'
import {IssueCreationKind, instanceOfIssueFormData} from './utils/model'
import {type IssueFormElementRef, IssueFormElements} from '@github-ui/issue-form'
import {SharedEmptyPicker} from '@github-ui/item-picker'
import {AlertIcon, TableIcon} from '@primer/octicons-react'
import {useIssueCreateContext} from './contexts/IssueCreateContext'
import {ContributorFooter} from '@github-ui/contributor-footer'
import {getPotentialFormDefaultValuesFromUrl} from './utils/urls'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {SafeOptionConfig} from './utils/option-config'

type CreatedIssue = Exclude<Exclude<createIssueMutation['response']['createIssue'], null>['issue'], null>
export type OnCreateProps = {
  issue: CreatedIssue
  createMore: boolean
}

export type CreateIssueCallbackProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
  onCancel: () => void
}

export type CreateIssueFormProps = {
  repository: Repository
  selectedTemplate?: IssueCreatePayload
  title: string
  setTitle: (title: string) => void
  body: string
  setBody: (body: string) => void
  labels: Label[]
  setLabels: (labels: Label[]) => void
  projects: Project[]
  setProjects: (projects: Project[]) => void
  assignees: Assignee[]
  setAssignees: (assignees: Assignee[]) => void
  milestone: Milestone | null
  setMilestone: (milestone: Milestone | null) => void
  issueType: IssueType | null
  setIssueType: (issueType: IssueType | null) => void
  clearOnCreate: () => void
  insidePortal: boolean
  focusTitleInput?: boolean
  optionConfig: SafeOptionConfig
} & CreateIssueCallbackProps

export const CreateIssueForm = ({
  repository,
  title,
  setTitle,
  body,
  setBody,
  labels,
  setLabels,
  projects,
  setProjects,
  assignees,
  setAssignees,
  milestone,
  setMilestone,
  issueType,
  setIssueType,
  clearOnCreate,
  onCreateSuccess,
  selectedTemplate,
  focusTitleInput,
  insidePortal,
  optionConfig,
}: CreateIssueFormProps): JSX.Element => {
  const titleInputRef = useRef<HTMLInputElement>(null)

  const [viewMode, setViewMode] = useState<ViewMode>('edit')
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)
  const [bodyValidationResult, setBodyValidationResult] = useState<string | undefined>(undefined)

  const {addToast} = useToastContext()
  const {
    createMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    isSubmitting,
    setIsSubmitting,
    onCreateAction,
    issueFormRef,
  } = useIssueCreateContext()
  const environment = useRelayEnvironment()
  const issueTypesFFEnabled = useFeatureFlag('issue_types')
  const {storageKeyPrefix} = useIssueCreateContext()

  const markdownEditorConfig: MarkdownEditorConfig = {
    pasteUrlsAsPlainText: optionConfig.pasteUrlsAsPlainText,
    useMonospaceFont: optionConfig.useMonospaceFont,
  }

  useEffect(() => {
    // Focus title input on first render
    // TextInput mutates the ref so we only focus on first render
    if (!isSubmitting && focusTitleInput) titleInputRef?.current?.focus()
  }, [focusTitleInput, isSubmitting])

  const clearFields = useCallback(() => {
    setTitle('')
    setBody('')
    issueFormRef.current?.resetInputs()
  }, [issueFormRef, setBody, setTitle])

  useImperativeHandle(
    onCreateAction,
    () => ({
      onCreate: (isSubmittingArg, createMoreArg) => {
        if (isSubmittingArg) return

        let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

        const inputValidationResult = validateIssueTitle(title)
        setTitleValidationResult(inputValidationResult.errorMessage)
        if (!inputValidationResult.isValid && titleInputRef.current) {
          refToFocusOnError = titleInputRef.current
        }

        const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)
        let issueBodyToSave = body
        if (isIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          // If there are validation errors, use first input if there's no ref yet
          if (invalidInputs.length > 0) {
            refToFocusOnError = refToFocusOnError || invalidInputs[0]
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        // We validate the length === 0 also for the initial state, in which the validation result will be undefined, too.
        if (
          refToFocusOnError ||
          titleValidationResult !== undefined ||
          (title ?? '').trim().length === 0 ||
          repository === undefined ||
          bodyValidationResult !== undefined
        ) {
          // If there is an input with an error, focus it
          refToFocusOnError?.focus()
          return
        }

        setIsSubmitting(true)
        let issueTemplate = undefined
        if (
          selectedTemplate &&
          (selectedTemplate.kind === IssueCreationKind.IssueTemplate ||
            selectedTemplate.kind === IssueCreationKind.IssueForm)
        ) {
          issueTemplate = selectedTemplate.name
        }

        const input: CreateIssueInput = {
          repositoryId: repository.id,
          title: title ? title.trim() : '',
          body: issueBodyToSave,
          labelIds: labels.length > 0 ? labels.map(label => label.id) : undefined,
          assigneeIds: assignees.length > 0 ? assignees.map(assignee => assignee.id) : undefined,
          milestoneId: milestone?.id,
          issueTypeId: issueTypesFFEnabled ? issueType?.id : null,
          issueTemplate,
        }

        commitCreateIssueMutation({
          environment,
          input,
          onError: (error: Error) => {
            reportError(formatError(error.message))
            setIsSubmitting(false)
            addToast({
              type: 'error',
              message: ERRORS.createIssueError,
            })
          },
          onCompleted: response => {
            setIsSubmitting(false)
            if (!response.createIssue?.issue) {
              response.createIssue?.errors.map(e => reportError(formatError(e.message)))
              addToast({
                type: 'error',
                message: ERRORS.createIssueError,
              })
              return
            }

            const newIssue = response.createIssue.issue

            if (projects.length > 0) {
              assignIssueToProjects(newIssue.id, projects, environment)
            }

            if (createMoreArg) {
              setViewMode('edit')
              setCreateMoreCreatedPath({...createMoreCreatedPath, number: newIssue.number})
            } else {
              clearOnCreate()
            }

            clearFields()
            onCreateSuccess({issue: newIssue, createMore: createMoreArg})
          },
        })
      },
    }),
    [
      title,
      selectedTemplate,
      body,
      issueFormRef,
      titleValidationResult,
      repository,
      bodyValidationResult,
      setIsSubmitting,
      labels,
      assignees,
      milestone?.id,
      issueTypesFFEnabled,
      issueType?.id,
      environment,
      addToast,
      projects,
      clearFields,
      onCreateSuccess,
      setCreateMoreCreatedPath,
      createMoreCreatedPath,
      clearOnCreate,
    ],
  )

  const handleTitleChange = (e: FormEvent<HTMLInputElement>) => {
    const inputTitle = e.currentTarget.value
    validateAndSetTitle(inputTitle)
  }

  const validateAndSetTitle = (newTitle: string) => {
    const inputValidationResult = validateIssueTitle(newTitle)
    // when title changes, check if we need to clear validation error
    // validation error are only added when user tries to create the issue
    if (inputValidationResult.isValid) {
      setTitleValidationResult(undefined)
    }
    setTitle(newTitle)
  }
  const handleBodyChange = (inputBody: string) => {
    validateAndSetBody(inputBody)
  }
  const validateAndSetBody = (newBody: string) => {
    const inputValidationResult = validateIssueBody(newBody)

    setBodyValidationResult(inputValidationResult.errorMessage)
    setBody(newBody)
  }

  const [labelsRef, loadLabels, disposeLabels] = useQueryLoader<LabelPickerQuery>(LabelPickerGraphqlQuery)
  const [projectsRef, loadProjects, disposeProjects] = useQueryLoader<ProjectPickerQuery>(ProjectPickerGraphqlQuery)
  const [milestonesRef, loadMilestones, disposeMilestones] =
    useQueryLoader<MilestonePickerQuery>(MilestonesPickerGraphqlQuery)
  const [issueTypesRef, loadIssueTypes, disposeIssueTypes] =
    useQueryLoader<IssueTypePickerQuery>(IssueTypePickerGraphqlQuery)

  useEffect(() => {
    if (repository) {
      loadLabels({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadProjects({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      loadMilestones({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
      if (issueTypesFFEnabled)
        loadIssueTypes({owner: repository.owner.login, repo: repository.name}, {fetchPolicy: 'store-or-network'})
    }
    return () => {
      disposeLabels()
      disposeProjects()
      disposeMilestones()
      disposeIssueTypes()
    }
  }, [
    repository,
    disposeLabels,
    disposeMilestones,
    loadLabels,
    loadMilestones,
    disposeProjects,
    loadProjects,
    disposeIssueTypes,
    loadIssueTypes,
    issueTypesFFEnabled,
  ])

  const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)

  const setMilestoneCallback = useCallback(
    (milestones: Milestone[] | null) => {
      setMilestone(milestones?.[0] || null)
    },
    [setMilestone],
  )

  const setIssueTypeCallback = useCallback(
    (issueTypes: IssueType[] | null) => {
      setIssueType(issueTypes?.[0] || null)
    },
    [setIssueType],
  )

  const subject = useMemo<Subject | undefined>(() => {
    if (repository) {
      return {
        type: 'issue',
        repository: {
          databaseId: repository.databaseId!,
          nwo: `${repository.owner.login}/${repository.name}`,
          slashCommandsEnabled: repository.slashCommandsEnabled,
        },
      }
    }
  }, [repository])

  const canAssign = repository?.viewerIssueCreationPermissions?.assignable
  const canLabel = repository?.viewerIssueCreationPermissions?.labelable
  const canMilestone = repository?.viewerIssueCreationPermissions?.milestoneable
  const canProject = repository?.viewerIssueCreationPermissions?.triageable
  const canIssueType =
    issueTypesFFEnabled && repository?.viewerIssueCreationPermissions?.triageable && repository?.issueTypes !== null

  const validationErrorId = useId()
  const singleKeyShortcutsEnabled = optionConfig.singleKeyShortcutsEnabled

  return (
    <>
      <FormControl sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch'}} required>
        <FormControl.Label
          sx={{'> span > span:last-of-type': {color: 'var(--fgColor-danger, var(--color-danger-fg))'}}}
        >
          {LABELS.issueCreateTitleLabel}
        </FormControl.Label>
        <TextInput
          ref={titleInputRef}
          aria-label={LABELS.issueCreateTitleLabel}
          placeholder={LABELS.issueCreateTitlePlaceholder}
          value={title}
          onBlur={handleTitleChange}
          onChange={handleTitleChange}
          data-hpc
        />
        {titleValidationResult && (
          <FormControl.Validation variant="error">{titleValidationResult}</FormControl.Validation>
        )}
      </FormControl>
      {subject && !isIssueForm && (
        <>
          <MarkdownEditor
            subject={subject}
            label={LABELS.issueCreateBodyLabel}
            showLabel={true}
            placeholder={LABELS.issueBodyPlaceholder}
            viewMode={viewMode}
            onChangeViewMode={setViewMode}
            value={body as SafeHTMLString}
            onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
            onChange={handleBodyChange}
            saveButtonTrailingIcon={false}
            userSettings={markdownEditorConfig}
            minHeightLines={insidePortal ? 14 : 20}
            aria-describedby={bodyValidationResult ? validationErrorId : undefined}
          />
          {bodyValidationResult && (
            <Flash variant="danger" id={validationErrorId}>
              <Octicon icon={AlertIcon} />
              {bodyValidationResult}
            </Flash>
          )}
        </>
      )}

      {subject && isIssueForm && instanceOfIssueFormData(selectedTemplate.data) && (
        <IssueFormElements
          outputRef={issueFormRef}
          issueFormRef={selectedTemplate.data}
          subject={subject}
          markdownEditorConfig={markdownEditorConfig}
          sessionStorageKey={storageKeyPrefix}
          defaultValuesById={getPotentialFormDefaultValuesFromUrl()}
        />
      )}
      <Suspense fallback={<MetadataLoading />}>
        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap'}}>
          {repository && (canAssign || assignees.length > 0) && (
            <AssigneeRepositoryPicker
              repoName={repository.name}
              repoOwner={repository.owner.login}
              readonly={!canAssign}
              assignees={assignees}
              participants={[]}
              onSelectionChange={setAssignees}
              insidePortal={insidePortal}
              shortcutEnabled={singleKeyShortcutsEnabled}
            />
          )}
          {labelsRef && (canLabel || labels.length > 0) && (
            <LabelPickerBase
              readonly={!canLabel}
              labels={labels}
              labelQueryRef={labelsRef}
              onSelectionChange={setLabels}
              insidePortal={insidePortal}
              shortcutEnabled={singleKeyShortcutsEnabled}
            />
          )}
          {projectsRef && !optionConfig.scopedProjectTitle && (canProject || projects.length > 0) && (
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              projectQueryRef={projectsRef}
              readonly={!canProject}
              onSave={(selectedProjects: Project[]) => {
                setProjects(selectedProjects)
              }}
              selectedProjects={projects}
              firstSelectedProjectTitle={projects.length > 0 ? projects[0]!.title : ''}
              insidePortal={insidePortal}
              shortcutEnabled={singleKeyShortcutsEnabled}
              owner={repository.owner.login}
              repo={repository.name}
            />
          )}
          {projectsRef && optionConfig.scopedProjectTitle && (
            <SharedEmptyPicker label={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
          )}
          {milestonesRef && (canMilestone || milestone !== null) && (
            <MilestonePickerBase
              readonly={!canMilestone}
              activeMilestone={milestone}
              milestoneQueryRef={milestonesRef}
              onSelectionChange={setMilestoneCallback}
              insidePortal={insidePortal}
              shortcutEnabled={singleKeyShortcutsEnabled}
            />
          )}
          {issueTypesRef && canIssueType && (
            <IssueTypePickerBase
              readonly={false}
              activeIssueType={issueType}
              issueTypeQueryRef={issueTypesRef}
              onSelectionChange={setIssueTypeCallback}
              insidePortal={insidePortal}
              shortcutEnabled={singleKeyShortcutsEnabled}
            />
          )}
        </Box>
      </Suspense>
      <ContributorFooter
        codeOfConductFileUrl={repository.codeOfConductFileUrl ?? undefined}
        securityPolicyUrl={repository.securityPolicyUrl ?? undefined}
        contributingFileUrl={repository.contributingFileUrl ?? undefined}
      />
    </>
  )
}

function assignIssueToProjects(issueId: string, projects: Project[], environment: Environment) {
  for (const project of projects) {
    commitUpdateIssueProjectsMutation({environment, issueId, projectId: project.id})
  }
}

function formatError(message: string) {
  return new Error(`Issue create mutation failed with error: ${message}`)
}

try{ CreateIssueForm.displayName ||= 'CreateIssueForm' } catch {}